import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MarkerClassCard } from "ui/base/class/MarkerClassCard";
import { getAvatarUrl, getLowestValidPrice } from "utils";
import { debounce, throttle } from "utils/func";
import { mapStyle } from "conts/mapbox";
import { fonts, mediaSizes } from "ui/theme";
import {useNavigate} from "react-router-dom";
import {userRoute} from "../../user.routes";

export const Map = ({ typeOfClass, location, classes, setMapCenter }) => {
    const navigate = useNavigate();
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRefs = useRef([]);

    const [zoom, setZoom] = useState(0);
    const [mapError, setMapError] = useState(null);

    useEffect(() => {
        if (mapInstanceRef.current) return;

        try {
            const token = process.env.REACT_APP_MAPBOX_TOKEN;
            if (!token) {
                throw new Error("Mapbox token missing! Set REACT_APP_MAPBOX_TOKEN.");
            }

            mapboxgl.accessToken = token;

            const map = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: mapStyle,
                center: [location.longitude, location.latitude],
                zoom: 11,
            });

            map.addControl(new mapboxgl.NavigationControl(), "top-right");

            const updateMapCenter = debounce(() => {
                const mapCenter = map.getCenter();
                setMapCenter({
                    latitude: mapCenter.lat,
                    longitude: mapCenter.lng,
                });
            }, 500);

            map.on("move", updateMapCenter);

            const updateZoom = throttle(() => {
                setZoom(map.getZoom());
            }, 500);
            map.on("zoom", updateZoom);

            mapInstanceRef.current = map;

            return () => {
                updateMapCenter.cancel && updateMapCenter.cancel();
                map.remove();
            };
        } catch (error) {
            console.error("Map initialization error:", error);
            setMapError(error.message);
        }
    }, []);

    useEffect(() => {
        const mapInstance = mapInstanceRef.current;
        if (!mapInstance || !classes.length) return;

        markerRefs.current?.forEach((marker) => marker.remove());
        markerRefs.current = [];

        const seenPositions = new Set();

        classes.forEach((item, index) => {
            const positionKey = `${item.lng}-${item.lat}`;
            let [lng, lat] = [+item.lng, +item.lat];

            if (seenPositions.has(positionKey)) {
                const maxZoom = 25.5;
                const minOffset = 0.00000001;
                const maxOffset = 0.002;

                const zoomFactor =
                    minOffset + (maxOffset - minOffset) * Math.pow(1 - zoom / maxZoom, 3);

                const angle = index * 137.5 * (Math.PI / 180);

                const indexFactor = Math.pow(index, 0.6);

                lng += Math.cos(angle) * zoomFactor * indexFactor;
                lat += Math.sin(angle) * zoomFactor * indexFactor;
            }

            seenPositions.add(positionKey);

            const markerElement = document.createElement("div");
            markerElement.innerHTML = MarkerClassCard({
                zoomLevel: zoom,
                classData: {
                    img: getAvatarUrl(item.profile_pic || ""),
                    rate: item.rate || 0,
                    review: item.review || 0,
                    title: item.subject_sub_category,
                    price: getLowestValidPrice(
                        item?.type_of_class,
                        item?.price_per_group_class,
                        item?.price_per_private_class
                    )
                },
            });

            markerElement.addEventListener("click", () => {
                if(zoom > 12){
                    navigate(userRoute.classDetail + item.user_id)
                    return
                }
                mapInstance.flyTo({
                    center: [lng, lat],
                    zoom: 16,
                    essential: true,
                });
            });

            const marker = new mapboxgl.Marker({ element: markerElement })
                .setLngLat([lng, lat])
                .addTo(mapInstance);

            markerRefs.current.push(marker);
        });

        return () => {
            markerRefs.current?.forEach((marker) => marker.remove());
        };
    }, [classes, zoom]);

    if (mapError) {
        return (
            <ErrorContainer>
                <ErrorMessage>{mapError}</ErrorMessage>
            </ErrorContainer>
        );
    }

    return (
        <Container>
            <Title>{typeOfClass} </Title>
            <MapContainer ref={mapContainerRef} />
        </Container>
    );
};

const Container = styled.div`
    display: grid;
    padding-block: 1rem;
    width: 100%;
    height: 100%;
    gap: 1rem;
    order: 1;
    @media (width >= ${mediaSizes.lg}) {
        order: 2;
    }
`;

const Title = styled.h2`
    font-size: ${fonts.sizes.big};
    font-weight: 600;


    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.h4};
    }
    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes["h3.4"]};
        display: none;
    }
`;

const MapContainer = styled.div`
    width: 100%;
    height: 100%;
    min-height: 500px;
    border-radius: 5px;
`;

const ErrorContainer = styled.div`
    padding: 2rem;
    text-align: center;
    background: #f8f9fa;
    border-radius: 5px;
`;

const ErrorMessage = styled.div`
    color: #dc3545;
`;

export default Map;
